import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useCallback, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  useGetSaltoSpaceAccessPoints,
  useInvalidateSaltoSpaceAccessPoints,
} from '@/api/keyless/salto-space/get-salto-space-access-points'
import { PasswordInputFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { TabActions } from '@/components/layout/tab-card'
import { Button } from '@/components/ui/button'
import { toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { getFormChanges } from '../../../../shared/form-changes'
import { ProviderCard } from './provider-card'
import { useSaltoSpaceCredentials, useSaveSaltoSpaceCredentials } from './provider-connections'
import { ProviderField } from './provider-field'
import { SaltoAccessGroup } from './salto-access-group'
import { useSaltoChangeFields, useSaltoSpaceSchema } from './salto-space.hooks'
import { SaltoFormData } from './salto-space.types'
import { getDefaults, toItems, withStoredItems } from './salto-space.utils'
import { SecretValue } from './secret-value'
import { ProviderDirtyChange, useProviderEdit } from './use-provider-edit'

interface SaltoSpaceCardProps {
  buildingId: number
  onDirtyChange: ProviderDirtyChange
  tabs: ReactNode
}

export const SaltoSpaceCard = ({ buildingId, onDirtyChange, tabs }: SaltoSpaceCardProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const invalidateAccessPoints = useInvalidateSaltoSpaceAccessPoints()
  const accessLabels = [
    t('organisation:places.integrations.salto.zone.label'),
    t('organisation:places.integrations.salto.accessLevel.label'),
  ]
  const skeletonGroups = [
    {
      labels: [
        t('organisation:places.integrations.salto.urlField.label'),
        t('organisation:places.integrations.salto.apiKeyField.label'),
      ],
    },
    {
      labels: [
        t('organisation:places.integrations.salto.autoAssignResidentDoorAccess'),
        ...accessLabels,
      ],
      title: t('organisation:places.integrations.salto.residentAccesses'),
    },
    {
      labels: [
        t('organisation:places.integrations.salto.autoAssignVisitorDoorAccess'),
        ...accessLabels,
      ],
      title: t('organisation:places.integrations.salto.visitorAccesses'),
    },
  ]

  const { integration, isLoading } = useSaltoSpaceCredentials(buildingId)
  const configured = !!integration?.configured
  const { data: accessPoints, isLoading: isAccessLoading } = useGetSaltoSpaceAccessPoints({
    buildingId,
  })
  const { mutateAsync: saveIntegration, isPending: isSaving } = useSaveSaltoSpaceCredentials()

  const schema = useSaltoSpaceSchema(configured)

  const serverValues = useMemo(() => getDefaults(integration), [integration])

  const form = useForm<SaltoFormData>({
    defaultValues: serverValues,
    mode: 'onTouched',
    resetOptions: { keepDirtyValues: true },
    resolver: zodResolver(schema),
    values: serverValues,
  })

  const zones = accessPoints?.permissionGroups ?? []
  const levels = accessPoints?.accessLevels ?? []

  const zoneOptions = useMemo(
    () =>
      withStoredItems(zones, [
        ...(integration?.config.permissionGroups ?? []),
        ...(integration?.config.visitorPermissionGroups ?? []),
      ]),
    [zones, integration],
  )
  const levelOptions = useMemo(
    () =>
      withStoredItems(levels, [
        ...(integration?.config.accessLevels ?? []),
        ...(integration?.config.visitorAccessLevels ?? []),
      ]),
    [levels, integration],
  )

  const changeFields = useSaltoChangeFields(zoneOptions, levelOptions)

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  const { cancel, isEditing, start, stop } = useProviderEdit({
    form,
    getChanges,
    onDirtyChange,
    serverValues,
  })

  const onSubmit: SubmitHandler<SaltoFormData> = async (values) => {
    const config = {
      accessLevels: toItems(values.residentAccessLevels, levelOptions),
      permissionGroups: toItems(values.residentZones, zoneOptions),
      shouldAutoAssignApartmentDoor: values.shouldAutoAssignApartmentDoor,
      shouldAutoAssignUserAccessLevel: values.shouldAutoAssignUserAccessLevel,
      visitorAccessLevels: toItems(values.visitorAccessLevels, levelOptions),
      visitorPermissionGroups: toItems(values.visitorZones, zoneOptions),
    }

    const apiKey = configured ? integration.apiKey : values.apiKey.trim()
    const url = configured ? integration.url : values.url.trim()

    try {
      // Note: the backend needs the stored credentials again on every save
      await saveIntegration({ apiKey, buildingId, config, url })
      toastFactory.success(t('organisation:places.integrations.keyless.toasts.saved'))
      form.reset({ ...values, apiKey, url })
      stop()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSave = useFormSave({ form, isEdit: configured, onSubmit, onUnchanged: stop })

  const emptyMessage = isAccessLoading
    ? t('organisation:places.integrations.salto.emptyOptions.loading')
    : configured
      ? t('organisation:places.integrations.salto.emptyOptions.syncing')
      : t('organisation:places.integrations.salto.emptyOptions.notConnected')

  const isSyncing = configured && !isAccessLoading && zones.length === 0 && levels.length === 0

  const notes = configured ? [] : [t('organisation:places.accessControl.credentials.saltoNote')]

  return (
    <FormProvider {...form}>
      <ProviderCard
        actions={
          <>
            {isSyncing && (
              <Button size="small" use="secondary" onClick={invalidateAccessPoints}>
                {t('organisation:places.integrations.salto.refreshData')}
              </Button>
            )}
            <TabActions
              isEditing={isEditing}
              isSaving={isSaving}
              onCancel={cancel}
              onEdit={start}
              onSave={handleSave}
            />
          </>
        }
        configured={configured}
        loading={isLoading}
        notes={notes}
        skeletonGroups={skeletonGroups}
        tabs={tabs}
      >
        <ProviderField
          required
          isEditing={isEditing}
          label={t('organisation:places.integrations.salto.urlField.label')}
          locked={configured}
          view={serverValues.url}
        >
          <TextInputFormField
            clearable
            withRestore
            autoComplete="off"
            name="url"
            placeholder={t('organisation:places.integrations.salto.urlField.placeholder')}
            size="small"
          />
        </ProviderField>
        <ProviderField
          required
          isEditing={isEditing}
          label={t('organisation:places.integrations.salto.apiKeyField.label')}
          locked={configured}
          view={serverValues.apiKey ? <SecretValue value={serverValues.apiKey} /> : null}
        >
          <PasswordInputFormField
            autoComplete="new-password"
            name="apiKey"
            placeholder={t('organisation:places.integrations.salto.apiKeyField.placeholder')}
            size="small"
          />
        </ProviderField>
        {(['resident', 'visitor'] as const).map((group) => (
          <SaltoAccessGroup
            key={group}
            emptyMessage={emptyMessage}
            group={group}
            isAccessLoading={isAccessLoading}
            isEditing={isEditing}
            levelOptions={levelOptions}
            serverValues={serverValues}
            zoneOptions={zoneOptions}
          />
        ))}
      </ProviderCard>
    </FormProvider>
  )
}
